/**
 * Roles and permissions. Talks to the /rbac group, which in phase 1 only stores
 * and reports roles — it changes nobody's access yet. See docs/PERMISSIONS_PLAN.md.
 *
 * Non-namespaced like every other module here. Arrays go over the wire as JSON
 * strings because the API speaks form-urlencoded and decodes them itself.
 */
const notifyError = (commit, message) =>
  commit("NOTIFICATION_MESSAGE", { status: -1, title: "Roles", message });

const form = (obj) => {
  const p = new URLSearchParams();
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    p.append(k, Array.isArray(v) || (v && typeof v === "object") ? JSON.stringify(v) : v);
  });
  return p;
};

const state = () => ({
  mePromise: null,
  catalog: [],
  roles: [],
  users: [],
  rolesLoaded: false,
  usersLoaded: false,
  // A failed load has to be visible on the page. The toast lasts two seconds;
  // a blank table lasts until someone refreshes and wonders why.
  rolesError: "",
  usersError: "",
  // Who the signed-in person is and what they may do, from GET rbac/me.
  // `permissions` is null until the API has answered (or when the API does not
  // have RBAC yet); null means "no restriction known", so the panel behaves
  // exactly as before instead of hiding everything.
  me: { loaded: false, role: "", roleName: "", permissions: null, allStores: true, stores: [] },
  // The store editor on the Users page: brand stores + the chosen user's links.
  userStores: { uid: 0, allStores: true, storeIds: [], brandStores: [], loaded: false },
});

const mutations = {
  /** Roles and users lists: cleared at sign-out so the next person never sees the previous list. */
  RESET_RBAC_LISTS(s) { const f = state(); s.roles = f.roles; s.users = f.users; s.userStores = f.userStores; s.catalog = f.catalog; },
  SET_RBAC_CATALOG(s, v) { s.catalog = v || []; },
  SET_RBAC_ROLES(s, v) { s.roles = v || []; s.rolesLoaded = true; s.rolesError = ""; },
  SET_RBAC_USERS(s, v) { s.users = v || []; s.usersLoaded = true; s.usersError = ""; },
  SET_RBAC_ROLES_ERROR(s, m) { s.rolesError = m; s.rolesLoaded = true; },
  SET_RBAC_ME(s, v) {
    const set = {};
    (v.permissions || []).forEach((k) => { set[k] = true; });
    s.me = {
      loaded: true, role: v.role || "", roleName: v.role_name || v.role || "", permissions: set,
      allStores: v.all_stores !== false, stores: v.stores || [],
    };
  },
  RESET_RBAC_ME(s) { s.me = { loaded: false, role: "", roleName: "", permissions: null, allStores: true, stores: [] }; },
  SET_RBAC_USER_STORES(s, v) { s.userStores = { uid: v.uid, allStores: !!v.all_stores, storeIds: (v.store_ids || []).map(Number), brandStores: v.brand_stores || [], loaded: true }; },
  RESET_RBAC_USER_STORES(s) { s.userStores = { uid: 0, allStores: true, storeIds: [], brandStores: [], loaded: false }; },
  SET_RBAC_USERS_ERROR(s, m) { s.usersError = m; s.usersLoaded = true; },
};

const getters = {
  /** can("coupons.create") — true while permissions are unknown, so nothing breaks before the API is deployed. */
  can: (s) => (perm) => !s.me.permissions || !!s.me.permissions[perm],
  myRole: (s) => s.me,
  /**
   * Whether this person may look at "All stores" (the brand's totals): someone linked to every store, or a super, whom
   * the API never narrows. On Megabite's shared database "every store" counts other brands' stores too, so a super there
   * is not all_stores. The API answers s=all with that person's own linked stores of their brand either way.
   */
  mayPickAllStores: (s) => !!(s.me.loaded && (s.me.allStores || s.me.role === "super")),
};

const actions = {
  /** Called once after sign-in and on every full page load. Quietly does nothing on an API without RBAC. */
  async getMe({ commit, state: s }) {
    if (s.mePromise) return s.mePromise;   // Dashboard and Home both ask at sign-in; one request serves both
    s.mePromise = (async () => {
      try {
        const { data } = await window.$http.axios.get("rbac/me", { silent: true });
        if (data.status) commit("SET_RBAC_ME", data);
      } catch (e) {
        // 404 = API without /rbac yet. Leave permissions null: no restriction.
      } finally { s.mePromise = null; }
    })();
    return s.mePromise;
  },
  async getRbacUserStores({ commit }, uid) {
    commit("RESET_RBAC_USER_STORES");
    try {
      const { data } = await window.$http.axios.get("rbac/users/stores", { params: { uid } });
      if (data.status) commit("SET_RBAC_USER_STORES", data);
      else notifyError(commit, data.message);
    } catch (e) {
      notifyError(commit, "Could not load that person's stores.");
    }
  },
  async saveRbacUserStores({ commit, dispatch }, { uid, storeIds }) {
    try {
      const { data } = await window.$http.axios.post("rbac/users/stores/save", form({ uid, store_ids: storeIds }));
      if (data.status) {
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Users", message: "Stores saved." });
        dispatch("getRbacUsers");
        return true;
      }
      notifyError(commit, data.message);
      return false;
    } catch (e) {
      notifyError(commit, "Could not save the stores.");
      return false;
    }
  },
  async getRbacCatalog({ commit, state: s }) {
    if (s.catalog.length) return;
    try {
      const { data } = await window.$http.axios.get("rbac/catalog");
      if (data.status) commit("SET_RBAC_CATALOG", data.catalog);
      else notifyError(commit, data.message);
    } catch (e) {
      notifyError(commit, "Could not load the permission list.");
    }
  },
  async getRbacRoles({ commit }) {
    commit("LOADING", { isActiveStatus: true });
    try {
      const { data } = await window.$http.axios.get("rbac/roles");
      if (data.status) commit("SET_RBAC_ROLES", data.roles);
      else { notifyError(commit, data.message); commit("SET_RBAC_ROLES_ERROR", data.message || "The server refused the request."); }
    } catch (e) {
      notifyError(commit, "Could not load roles.");
      commit("SET_RBAC_ROLES_ERROR", "Could not reach the permissions service. It may not be installed on this server yet.");
    }
    commit("LOADING", { isActiveStatus: false });
  },
  async saveRbacRole({ commit, dispatch }, role) {
    commit("LOADING", { isActiveStatus: true });
    let ok = false;
    try {
      const { data } = await window.$http.axios.post("rbac/roles/save", form(role));
      if (data.status) {
        ok = data.key || true;
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Roles", message: "Role saved." });
        await dispatch("getRbacRoles");
      } else notifyError(commit, data.message);
    } catch (e) {
      notifyError(commit, "Could not save the role.");
    }
    commit("LOADING", { isActiveStatus: false });
    return ok;
  },
  async deleteRbacRole({ commit, dispatch }, key) {
    commit("LOADING", { isActiveStatus: true });
    try {
      const { data } = await window.$http.axios.post("rbac/roles/delete", form({ key }));
      if (data.status) {
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Roles", message: "Role deleted." });
        await dispatch("getRbacRoles");
      } else notifyError(commit, data.message);
    } catch (e) {
      notifyError(commit, "Could not delete the role.");
    }
    commit("LOADING", { isActiveStatus: false });
  },
  async getRbacUsers({ commit }) {
    commit("LOADING", { isActiveStatus: true });
    try {
      const { data } = await window.$http.axios.get("rbac/users");
      if (data.status) commit("SET_RBAC_USERS", data.users);
      else { notifyError(commit, data.message); commit("SET_RBAC_USERS_ERROR", data.message || "The server refused the request."); }
    } catch (e) {
      notifyError(commit, "Could not load users.");
      commit("SET_RBAC_USERS_ERROR", "Could not reach the permissions service. It may not be installed on this server yet.");
    }
    commit("LOADING", { isActiveStatus: false });
  },
  async assignRbacRole({ commit, dispatch }, { uid, role }) {
    try {
      const { data } = await window.$http.axios.post(
        "rbac/users/assign",
        form({ uid, role })
      );
      if (data.status) {
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Users", message: "Role updated." });
        await dispatch("getRbacUsers");
        await dispatch("getRbacRoles");
      } else notifyError(commit, data.message);
    } catch (e) {
      notifyError(commit, "Could not change the role.");
    }
  },
};

export default { state, getters, mutations, actions };
